import React, { createContext, useContext, useState, useEffect } from 'react';
import { egyptianLocations, City, Area } from '@/data/locations';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Extended interface to handle both static and database city data
interface ExtendedCity extends City {
  name_en?: string;
  name_ar?: string;
  url_path?: string;
}

interface DeliveryLocationContextType {
  selectedCity: ExtendedCity | null;
  setSelectedCity: (city: ExtendedCity | null) => void;
  availableCities: City[];
}

const DeliveryLocationContext = createContext<DeliveryLocationContextType | undefined>(undefined);

export const useDeliveryLocation = () => {
  const context = useContext(DeliveryLocationContext);
  if (!context) {
    throw new Error('useDeliveryLocation must be used within a DeliveryLocationProvider');
  }
  return context;
};

interface DeliveryLocationProviderProps {
  children: React.ReactNode;
}

export const DeliveryLocationProvider: React.FC<DeliveryLocationProviderProps> = ({ children }) => {
  const [selectedCity, setSelectedCity] = useState<ExtendedCity | null>(null);
  const location = useLocation();

  // Fetch db cities for Arabic names
  const { data: dbCities = [] } = useQuery({
    queryKey: ['cities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cities')
        .select('name_en, name_ar, slug')
        .eq('is_active', true)
        .eq('countries.country_code', 'eg')
        .order('name_en');
      if (error) throw error;
      return data;
    }
  });

  useEffect(() => {
    const match = location.pathname.match(/^\/(en-eg|ar-eg)\/([^/]+)/);
    const urlCitySlug = match ? match[2] : null;
    const isArabic = location.pathname.startsWith('/ar-eg/');

    if (urlCitySlug) {
      let city = egyptianLocations.find(c => c.slug === urlCitySlug);
      if (city) {
        // Enhance with Arabic name if in Arabic mode
        if (isArabic) {
          const dbCity = dbCities.find((c: any) => c.slug === city!.slug);
          if (dbCity && dbCity.name_ar) {
            city = { ...city, name_ar: dbCity.name_ar };
          } else if (city.slug === 'cairo') {
            city = { ...city, name_ar: 'القاهرة' };
          } else if (city.slug === 'giza') {
            city = { ...city, name_ar: 'الجيزة' };
          } else if (city.slug === 'alexandria') {
            city = { ...city, name_ar: 'الإسكندرية' };
          } else if (city.slug === 'aswan') {
            city = { ...city, name_ar: 'أسوان' };
          } else if (city.slug === 'sharm-el-sheikh') {
            city = { ...city, name_ar: 'شرم الشيخ' };
          } else if (city.slug === 'luxor') {
            city = { ...city, name_ar: 'الأقصر' };
          } else if (city.slug === 'port-said') {
            city = { ...city, name_ar: 'بورسعيد' };
          } else if (city.slug === 'suez') {
            city = { ...city, name_ar: 'السويس' };
          } else if (city.slug === 'hurghada') {
            city = { ...city, name_ar: 'الغردقة' };
          } else if (city.slug === 'mansoura') {
            city = { ...city, name_ar: 'المنصورة' };
          } else if (city.slug === 'tanta') {
            city = { ...city, name_ar: 'طنطا' };
          } else if (city.slug === 'asyut') {
            city = { ...city, name_ar: 'أسيوط' };
          } else if (city.slug === 'ismailia') {
            city = { ...city, name_ar: 'الإسماعيلية' };
          } else if (city.slug === 'faiyum') {
            city = { ...city, name_ar: 'الفيوم' };
          } else if (city.slug === 'zagazig') {
            city = { ...city, name_ar: 'الزقازيق' };
          }
        }
        setSelectedCity(city);
        localStorage.setItem('selectedDeliveryCity', city.slug);
        return;
      }
    }

    // Fallback to localStorage
    const savedCitySlug = localStorage.getItem('selectedDeliveryCity');
    if (savedCitySlug) {
      const city = egyptianLocations.find(c => c.slug === savedCitySlug);
      if (city) {
        setSelectedCity(city);
      }
    }
  }, [location.pathname, dbCities]);

  // Save to localStorage when location changes
  useEffect(() => {
    if (selectedCity) {
      localStorage.setItem('selectedDeliveryCity', selectedCity.slug);
    } else {
      localStorage.removeItem('selectedDeliveryCity');
      localStorage.removeItem('selectedDeliveryArea'); // Clean up old area data
    }
  }, [selectedCity]);

  const value: DeliveryLocationContextType = {
    selectedCity,
    setSelectedCity,
    availableCities: egyptianLocations,
  };

  return (
    <DeliveryLocationContext.Provider value={value}>
      {children}
    </DeliveryLocationContext.Provider>
  );
};